import { Controller } from "@hotwired/stimulus"

// Single-page stepped wizard for campaign creation: Audience → Details →
// Compose → Review. All steps live in one <form> (alongside the existing
// contact-email-form controller, which still owns the fields/templates/summary).
// This controller only handles step navigation, per-step validation, and
// populating the read-only Review pane. The final Send/Schedule/Draft actions
// remain the existing buttons surfaced on the last step.
export default class extends Controller {
  static targets = [
    "step",        // each step pane (in order)
    "chip",        // stepper progress chips (in order)
    "backBtn",
    "nextBtn",
    "finalActions", // Send Campaign, shown on the last step
    "footerBanner", // "almost ready" banner, shown on the last step
    // Review pane fields:
    "reviewName", "reviewSubject", "reviewPreview", "reviewRecipients", "reviewAudiences",
    "reviewNever", "reviewUnsub", "reviewType", "reviewTone", "reviewLength", "reviewEvent",
    "reviewBody", "reviewEventCard", "reviewEventNote", "reviewTz"
  ]

  static values = {
    messages: Object, // { audience, subject, event, body } validation strings
    // Step index to open on. Defaults to 0 (Audience). The per-event
    // "Send Message" hub flow sets this to 1 (Details): the audience is already
    // decided (this event's attendees, pre-checked), so the organizer starts at
    // the content step instead of re-confirming a known cohort. Back still
    // reaches Audience.
    startStep: Number
  }

  connect() {
    const start = this.hasStartStepValue ? this.startStepValue : 0
    // Clamp so a stale/bad value can't open past the last step.
    this.current = Math.min(Math.max(start, 0), this.stepTargets.length - 1)
    this.render()
  }

  next() {
    if (!this.validateStep(this.current)) return
    if (this.current < this.stepTargets.length - 1) {
      this.current += 1
      if (this.isReviewStep) this.populateReview()
      this.render()
    }
  }

  back() {
    if (this.current > 0) {
      this.current -= 1
      this.render()
    }
  }

  // Jump straight to the Details step (step index 1) — used by the "Edit in
  // Details" link on the Compose step's read-only Subject/Preview header.
  goToDetails(event) {
    event?.preventDefault()
    this.current = 1
    this.render()
  }

  // Click a stepper chip to jump to that step. Backward (or same) is always
  // allowed; jumping forward validates the current step first, like Next.
  goToStep(event) {
    const target = parseInt(event.currentTarget.dataset.stepIndex, 10)
    if (Number.isNaN(target) || target === this.current) return
    if (target < this.current) {
      this.current = target
    } else {
      if (!this.validateStep(this.current)) return
      this.current = target
    }
    if (this.isReviewStep) this.populateReview()
    this.render()
  }

  get isReviewStep() {
    return this.current === this.stepTargets.length - 1
  }

  render() {
    this.stepTargets.forEach((pane, i) => pane.classList.toggle("d-none", i !== this.current))
    this.chipTargets.forEach((chip, i) => {
      chip.classList.toggle("active", i === this.current)
      chip.classList.toggle("done", i < this.current)
    })

    // Back hidden on the first step.
    if (this.hasBackBtnTarget) this.backBtnTarget.classList.toggle("d-none", this.current === 0)
    // Next on all but the last step; final actions only on the last.
    if (this.hasNextBtnTarget) this.nextBtnTarget.classList.toggle("d-none", this.isReviewStep)
    if (this.hasFinalActionsTarget) this.finalActionsTarget.classList.toggle("d-none", !this.isReviewStep)
    if (this.hasFooterBannerTarget) this.footerBannerTarget.classList.toggle("d-none", !this.isReviewStep)

    // Bring the stepper into view (not the whole wrapper) only when it's
    // actually scrolled off-screen — scrolling the wrapper to block:"start" on
    // every step change pushed the stepper nav off the top and made the content
    // jump under the fixed footer. The stepper stays visible so chips remain
    // clickable, and we never scroll when already in view.
    const stepper = this.element.querySelector(".campaign-wizard-stepper")
    if (stepper) {
      const top = stepper.getBoundingClientRect().top
      if (top < 0 || top > window.innerHeight) {
        stepper.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    }
  }

  // Per-step validation. Steps: 0 Audience, 1 Details, 2 Compose, 3 Review.
  validateStep(index) {
    const flash = (key) => {
      if (typeof window.displayFlashMessage === "function") {
        window.displayFlashMessage("error", this.messagesValue[key])
      }
    }

    if (index === 0) {
      const all = document.querySelector('input[name="campaign[include_all_contacts]"]')?.checked
      const groups = document.querySelectorAll('input[name="campaign[contact_group_ids][]"]:checked').length > 0
      const cohorts = document.querySelectorAll('input[name="campaign[cohorts][]"]:checked').length > 0
      // Only require that an audience is picked. Eligibility (Will Receive) is
      // shown prominently in the summary; we don't hard-block on it here (the
      // async summary may not have loaded yet, and zero-eligible is surfaced
      // visibly + handled server-side), which previously made Next feel broken.
      if (!all && !groups && !cohorts) { flash("audience"); return false }
      return true
    }

    // Step 1 (Details): focused metadata — only the subject is required here
    // (campaign name is optional; event moved to Compose).
    if (index === 1) {
      const subject = document.querySelector('input[name="campaign[subject]"]')?.value.trim()
      if (!subject) { flash("subject"); return false }
      return true
    }

    // Step 2 (Compose): event selection + body live here now.
    if (index === 2) {
      const eventId = document.querySelector('select[name="campaign[event_id]"]')?.value
      if (!eventId) { flash("event"); return false }
      // Promoted Session is OPTIONAL: leaving it blank promotes the whole series.
      // When set, it tells the AI/event card which occurrence to feature.
      const bodyEl = document.querySelector('trix-editor') || document.querySelector('input[name="campaign[body]"]')
      const body = bodyEl ? (bodyEl.innerHTML || bodyEl.value || "").trim() : ""
      // Trix renders an empty editor as "<div><br></div>"; treat that as blank.
      const isBlank = !body || body === "<div><br></div>" || bodyEl?.textContent?.trim() === ""
      if (isBlank) { flash("body"); return false }
      return true
    }

    return true
  }

  // Fill the read-only Review pane from the current form state.
  populateReview() {
    const text = (sel) => document.querySelector(sel)?.value?.trim() || ""
    const set = (target, val) => { if (target) target.textContent = val || "—" }
    const selectLabel = (name) => {
      const el = document.querySelector(`select[name="campaign[${name}]"]`)
      return el ? (el.options[el.selectedIndex]?.text || "").trim() : ""
    }
    const summaryText = (t) => document.querySelector(`[data-contact-email-form-target="${t}"]`)?.textContent.trim() || "0"

    // Campaign Details.
    const subject = text('input[name="campaign[subject]"]')
    if (this.hasReviewNameTarget) set(this.reviewNameTarget, text('input[name="campaign[name]"]') || subject)
    if (this.hasReviewSubjectTarget) set(this.reviewSubjectTarget, subject)
    if (this.hasReviewPreviewTarget) set(this.reviewPreviewTarget, text('input[name="campaign[preview_text]"]'))
    if (this.hasReviewToneTarget) set(this.reviewToneTarget, selectLabel("tone"))
    if (this.hasReviewLengthTarget) set(this.reviewLengthTarget, selectLabel("length"))
    if (this.hasReviewTypeTarget) {
      const card = document.querySelector('input[name="campaign[category]"]:checked')
        ?.closest(".template-tile")?.querySelector(".template-tile-title")?.textContent?.trim()
      set(this.reviewTypeTarget, card)
    }
    if (this.hasReviewEventTarget) {
      const ev = document.querySelector('select[name="campaign[event_id]"]')
      set(this.reviewEventTarget, ev && ev.value ? (ev.options[ev.selectedIndex]?.text || "").trim() : "")
    }

    // Audience: eligible + excluded counts (mirror the summary), and labels.
    if (this.hasReviewRecipientsTarget) set(this.reviewRecipientsTarget, summaryText("summaryWillReceive"))
    if (this.hasReviewNeverTarget) this.reviewNeverTarget.textContent = summaryText("summaryNever")
    if (this.hasReviewUnsubTarget) this.reviewUnsubTarget.textContent = summaryText("summaryUnsubscribed")
    if (this.hasReviewAudiencesTarget) {
      const labels = []
      document.querySelectorAll('.recipients-list .recipient-item').forEach((item) => {
        const cb = item.querySelector('input[type="checkbox"]')
        if (cb && cb.checked) {
          const label = item.querySelector('.heading, strong')?.textContent?.trim()
          if (label) labels.push(label)
        }
      })
      // Render as a bulleted list (split-view left column).
      this.reviewAudiencesTarget.innerHTML = labels.length
        ? labels.map((l) => `<li>${l.replace(/[<>&]/g, "")}</li>`).join("")
        : "<li>—</li>"
    }

    // Email preview: mirror the composed body, clone the event card, note.
    if (this.hasReviewBodyTarget) {
      const editor = document.querySelector('trix-editor')
      this.reviewBodyTarget.innerHTML = editor ? editor.innerHTML : (text('input[name="campaign[body]"]'))
    }
    // include_event_card renders a hidden "0" input + the checkbox under the same
    // name; query the checkbox specifically (the hidden one's .checked is false).
    const includeCard = document.querySelector('input[type="checkbox"][name="campaign[include_event_card]"]')?.checked
    const sourceCard = document.querySelector('#campaign_event_container .selected-event-wrapper')

    if (this.hasReviewEventCardTarget) {
      const show = includeCard && !!sourceCard
      this.reviewEventCardTarget.innerHTML = show && sourceCard ? sourceCard.outerHTML : ""
      this.reviewEventCardTarget.classList.toggle("d-none", !show)
    }
    if (this.hasReviewEventNoteTarget) {
      this.reviewEventNoteTarget.classList.toggle("d-none", !(includeCard && !!sourceCard))
    }
  }
}
